import React, { useState } from 'react';
import { RecordPaymentDetails } from '../driverCollectionsTypes';
import styles from './RecordPaymentForm.module.css';

interface RecordPaymentFormProps {
  details: RecordPaymentDetails;
  onConfirm?: (input: { amount: number; paymentType: string; reference: string; notes: string; partial: boolean; amountTendered?: number }) => void | Promise<void>;
  onCancel?: () => void;
  submitting?: boolean;
}

function money(n: number): string {
  return `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
}

export const RecordPaymentForm: React.FC<RecordPaymentFormProps> = ({ details, onConfirm, onCancel, submitting }) => {
  const [paymentType, setPaymentType] = useState('Cash');
  const [amount, setAmount] = useState('');
  const [partial, setPartial] = useState(false);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [tendered, setTendered] = useState('');

  const isCash = paymentType === 'Cash';
  const amountNum = parseFloat(amount.replace(/[^0-9.]/g, ''));
  const tenderedNum = parseFloat(tendered);
  const change = isCash && !isNaN(tenderedNum) && !isNaN(amountNum) ? tenderedNum - amountNum : null;

  const canConfirm =
    !submitting &&
    !!amount &&
    !isNaN(amountNum) &&
    amountNum > 0 &&
    (!isCash || (!isNaN(tenderedNum) && tenderedNum >= amountNum));

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>Record Payment - {details.deliveryId}</h3>

      <div className={styles.row}>
        <div className={styles.summaryBox}>
          <h4 className={styles.summaryTitle}>Payment Details</h4>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Customer</span>
            <span className={styles.summaryValue}>{details.customer}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Invoice #</span>
            <span className={styles.summaryValue}>{details.invoiceNumber}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Amount Due</span>
            <span className={`${styles.summaryValue} ${styles.amountDue}`}>{details.amountDue}</span>
          </div>
        </div>

        <div className={styles.formCol}>
          <div className={styles.field}>
            <label className={styles.label}>Payment Type</label>
            <select
              className={styles.select}
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
            >
              <option>Cash</option>
              <option>Check</option>
              <option>ACH</option>
              <option>Card</option>
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Amount Received</label>
            <input
              className={styles.input}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          {isCash && (
            <div className={styles.field}>
              <label className={styles.label}>Cash Tendered</label>
              <input
                className={styles.input}
                type="number"
                min={0}
                step="0.01"
                value={tendered}
                onChange={(e) => setTendered(e.target.value)}
                placeholder="0.00"
              />
            </div>
          )}

          {isCash && tendered !== '' && (
            <div className={styles.field}>
              <label className={styles.label}>Change Due</label>
              <span className={styles.summaryValue}>
                {change !== null ? money(change) : '—'}
              </span>
              {change !== null && change < 0 && (
                <p className={styles.label} style={{ color: '#DC2626' }}>
                  Tendered amount is less than what&apos;s due.
                </p>
              )}
            </div>
          )}

          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={partial}
              onChange={(e) => setPartial(e.target.checked)}
            />
            <span>Partial Payment</span>
          </label>
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Reference Number (for Check/ACH)</label>
        <input
          className={styles.input}
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Notes</label>
        <textarea
          className={styles.textarea}
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      <div className={styles.actions}>
        <button
          className={styles.confirmBtn}
          disabled={!canConfirm}
          onClick={() => {
            if (!isNaN(amountNum) && amountNum > 0) {
              onConfirm?.({
                amount: amountNum,
                paymentType,
                reference,
                notes,
                partial,
                amountTendered: isCash && !isNaN(tenderedNum) ? tenderedNum : undefined,
              });
            }
          }}
        >
          {submitting ? 'Recording…' : 'Confirm Payment'}
        </button>
        <button className={styles.cancelBtn} onClick={() => onCancel?.()}>Cancel</button>
      </div>
    </div>
  );
};
